import {action, computed, makeObservable, observable} from 'mobx';
import dayjs from 'dayjs';
import SaporeForm from '../../forms/SaporeForm';
import FormField from '../../forms/fields/FormField';
import OptionField from '../../forms/fields/OptionField';
import SubjectKindsPanels from './create_allowance/kinds/SubjectKindsPanels';
import AllowanceSubjectKindsPanels from './create_allowance/kinds/AllowanceSubjectKindsPanels';
import ResolutionKindsPanels from './create_allowance/kinds/ResolutionKindsPanels';

export default class Allowance {
    static forEdit({customerOptions, articleOptions, conceptOptions, franchiseBrandOptions}) {
        return new Allowance({customerOptions, articleOptions, conceptOptions, franchiseBrandOptions, readOnly: false});
    }

    static forDisplay(allowance) {
        const article = allowance.resolution.article;
        const subject = allowance.subject;
        return new Allowance({
            customerOptions: [],
            articleOptions: [],
            conceptOptions: [],
            readOnly: true,
            commonInitialValues: {
                concept: allowance.concept,
                validFrom: dayjs(allowance.validFrom),
                validTo: dayjs(allowance.validTo),
            },
            beneficiaryKind: allowance.beneficiary.kind,
            beneficiaryInitialValues: {customerId: allowance.beneficiary.businessName},
            subjectDiffersFromBeneficiary: !allowance.subjectIsTheBeneficiary,
            subjectKind: subject.kind,
            subjectInitialValues: new AllowanceSubjectKindsPanels().initialValuesFor(subject.kind, subject.businessName),
            resolutionKind: allowance.resolution.kind,
            resolutionInitialValues: {
                refundArticleId: article ? `${article.code} - ${article.name}` : '',
                percentage: allowance.resolution.percentage,
            },
        });
    }

    constructor({
                    customerOptions,
                    articleOptions,
                    conceptOptions,
                    franchiseBrandOptions = [],
                    readOnly = false,
                    commonInitialValues = {},
                    beneficiaryKind = null,
                    beneficiaryInitialValues = {},
                    subjectDiffersFromBeneficiary = false,
                    subjectKind = null,
                    subjectInitialValues = {},
                    resolutionKind = null,
                    resolutionInitialValues = {},
                }) {
        this._customerOptions = customerOptions;
        this._articleOptions = articleOptions;
        this._conceptOptions = conceptOptions;
        this._franchiseBrandOptions = franchiseBrandOptions;
        this._readOnly = readOnly;
        this._beneficiaryInitialValues = beneficiaryInitialValues;
        this._subjectInitialValues = subjectInitialValues;
        this._resolutionInitialValues = resolutionInitialValues;
        this._commonForm = new SaporeForm({
            concept: new OptionField(conceptOptions, false, undefined, commonInitialValues.concept),
            validFrom: new FormField(false, commonInitialValues.validFrom ?? ''),
            validTo: new FormField(false, commonInitialValues.validTo ?? ''),
        });
        this._beneficiaryKindsPanels = new SubjectKindsPanels();
        this._allowanceSubjectKindsPanels = new AllowanceSubjectKindsPanels();
        this._resolutionKindsPanels = new ResolutionKindsPanels();
        this._selectedSubjectKind = subjectKind;
        this._selectedBeneficiaryKind = beneficiaryKind;
        this._selectedResolutionKind = resolutionKind;
        this._subjectDiffersFromBeneficiary = subjectDiffersFromBeneficiary;

        makeObservable(this, {
            _selectedSubjectKind: observable,
            _selectedBeneficiaryKind: observable,
            _selectedResolutionKind: observable,
            _subjectDiffersFromBeneficiary: observable,
            selectedSubjectKind: computed,
            selectedBeneficiaryKind: computed,
            selectedResolutionKind: computed,
            subjectDiffersFromBeneficiary: computed,
            subjectForm: computed,
            beneficiaryForm: computed,
            resolutionForm: computed,
            isValid: computed,
            selectSubjectKind: action,
            selectBeneficiaryKind: action,
            selectResolutionKind: action,
            setSubjectDiffersFromBeneficiary: action,
        });
    }

    commonForm() {
        return this._commonForm;
    }

    isReadOnly() {
        return this._readOnly;
    }

    customerOptions() {
        return this._customerOptions;
    }

    articleOptions() {
        return this._articleOptions;
    }

    conceptOptions() {
        return this._conceptOptions;
    }

    franchiseBrandOptions() {
        return this._franchiseBrandOptions;
    }

    subjectKindOptions() {
        return this._allowanceSubjectKindsPanels.options();
    }

    subjectKindPanelFor(kind) {
        return this._allowanceSubjectKindsPanels.panelFor(kind);
    }

    get selectedSubjectKind() {
        return this._selectedSubjectKind;
    }

    selectSubjectKind(kind) {
        this._selectedSubjectKind = kind;
    }

    get subjectForm() {
        const kind = this._selectedSubjectKind;
        if (!kind) return null;
        const formOptions = {...this._formOptions(), initialValues: this._subjectInitialValues};
        const fields = this._allowanceSubjectKindsPanels.buildFieldsFor(kind, formOptions);
        return new SaporeForm(fields);
    }

    get selectedBeneficiaryKind() {
        return this._selectedBeneficiaryKind;
    }

    selectBeneficiaryKind(kind) {
        this._selectedBeneficiaryKind = kind;
    }

    get beneficiaryForm() {
        const kind = this._selectedBeneficiaryKind;
        if (!kind) return null;
        const formOptions = {...this._formOptions(), initialValues: this._beneficiaryInitialValues};
        const fields = this._beneficiaryKindsPanels.buildFieldsFor(kind, formOptions);
        return new SaporeForm(fields);
    }

    get selectedResolutionKind() {
        return this._selectedResolutionKind;
    }

    selectResolutionKind(kind) {
        this._selectedResolutionKind = kind;
    }

    get resolutionForm() {
        const kind = this._selectedResolutionKind;
        if (!kind) return null;
        const formOptions = {...this._formOptions(), initialValues: this._resolutionInitialValues};
        const fields = this._resolutionKindsPanels.buildFieldsFor(kind, formOptions);
        return new SaporeForm(fields);
    }

    get subjectDiffersFromBeneficiary() {
        return this._subjectDiffersFromBeneficiary;
    }

    setSubjectDiffersFromBeneficiary(value) {
        this._subjectDiffersFromBeneficiary = value;
    }

    get isValid() {
        const subjectValid = this._subjectDiffersFromBeneficiary ? this.subjectForm?.isValid : true;
        return this._commonForm.isValid
            && this.resolutionForm?.isValid
            && this.beneficiaryForm?.isValid
            && subjectValid;
    }

    payload() {
        const common = this._commonForm.values;
        const resolutionValues = this.resolutionForm?.values ?? {};
        const beneficiaryValues = this.beneficiaryForm?.values ?? {};
        const subjectValues = this.subjectForm?.values ?? {};
        const resolutionRequiresArticle = this._selectedResolutionKind === 'CREDIT_NOTE_REFUND_ON_ARTICLE';
        const subjectIsDistinctFromBeneficiary = this._subjectDiffersFromBeneficiary;
        const subjectIsAFranchise = subjectIsDistinctFromBeneficiary && this._selectedSubjectKind === 'FRANCHISE';
        return {
            conceptName: common.concept,
            validFrom: common.validFrom,
            validTo: common.validTo,
            beneficiaryKind: this._selectedBeneficiaryKind,
            customerBusinessName: beneficiaryValues.customerId,
            resolutionKind: this._selectedResolutionKind,
            articleCode: resolutionRequiresArticle ? resolutionValues.refundArticleId : null,
            percentage: resolutionValues.percentage,
            subjectKind: subjectIsDistinctFromBeneficiary ? this._selectedSubjectKind : null,
            franchiseBrandName: subjectIsAFranchise ? subjectValues.franchiseBrandId : null,
        };
    }

    _formOptions() {
        return {
            customerOptions: this._customerOptions,
            articleOptions: this._articleOptions,
            franchiseBrandOptions: this._franchiseBrandOptions,
        };
    }
}
